import type { SearchParams } from "../../common/types";
import { fetchData, fetchUniversitiesData } from "./config";

export const searchCourseReq = (params: Record<string, string | number>) => {
  return fetchData(`v2/search/courses_v2/`, params);
};

export const getCountryFilterDataReq = () => {
  return fetchData(`v2/search/locations/`);
};

export const getBlogsReq = () => {
  return fetchData(`blog`);
};

export const getBlogDetailReq = (slug: string) => {
  return fetchData(`blog/${slug}`);
};

export const getAllCoursesReq = () => {
  return fetchData(`v2/search/courses_v2/`);
};

export const getCourseDetails = (
  slug: string,
  duration_id: number,
  fee_id: number,
) => {
  if (!slug || !duration_id || !fee_id) {
    throw new Error("Missing required parameters");
  }
  return fetchData(`course/${slug}/${duration_id}/${fee_id}`);
};

export const getDegreeLevelsReq = () => {
  return fetchData(`v2/search/degree_levels/`);
};

export const getDisciplinesReq = () => {
  return fetchData(`v2/search/disciplines/`);
};
export const getDisciplinesCountReq = () => {
  return fetchData(`v2/search/disciplines-with-count/`);
};

export const getSearchDisciplinesReq = () => {
  return fetchData(`discipline/`);
};

export const getDisciplineSpecializationReq = (specialization: string) => {
  return fetchData(`discipline/${specialization}/specializations/`);
};

export const getSearchDegreeLevelReq = () => {
  return fetchData(`v2/search/degree_levels/`);
};

export const getSearchPopularCitiesReq = () => {
  return fetchData(`regions/popular-cities/`);
};

export const getSearchPopularCountriesReq = () => {
  return fetchData(`regions/popular-countries/`);
};

export const getSearchPopularRegionsReq = () => {
  return fetchData(`regions/all/`);
};

export const getSearchAllCountriesReq = () => {
  return fetchData(`country/all-countries/`);
};
export const getScholarshipDetails = (slug: string) => {
  if (!slug) {
    throw new Error("Missing required parameters");
  }
  return fetchData(`v2/search/scholarship-v2/${slug}`);
};
export const getAllScholarshipsReq = () => {
  return fetchData("scholarships/");
};

export const getcmsUniversityDetails = (slug: string) => {
  return fetchUniversitiesData(`v1/universities/${slug}/tcf`);
};

export const getAllSavedResources = (type: string) => {
  return fetchData(
    "v2/search/user-resources",
    { resource_type: type }, // Pass query params as an object
    "GET",
    undefined, // No body for GET requests
    true, // Enable credentials
  );
};

export const saveResources = (id: number, type: string) => {
  return fetchData(
    "v2/search/user-resources",
    {}, // Pass query params as an object
    "POST",
    { resource_id: id, resource_type: type }, // No body for GET requests
    true, // Enable credentials
  );
};

export const removeResources = (id: number, type: string) => {
  return fetchData(
    "v2/search/user-resources",
    {}, // Pass query params as an object
    "DELETE",
    { resource_id: id, resource_type: type }, // No body for GET requests
    true, // Enable credentials
  );
};

export const getoldUniversityDetails = (slug: string) => {
  return fetchData(`institute/${slug}/`);
};

export const getUniversityDetails = async (slug: string) => {
  if (!slug) {
    throw new Error("University slug is required");
  }

  const cmsData = await getcmsUniversityDetails(slug);

  if (!cmsData || !cmsData.sections || cmsData.sections.length === 0) {
    // If sections are empty, try fallback API

    const universityData = await getoldUniversityDetails(slug);
    return universityData;
  }
  return cmsData;
};
export const getCoursesCountReq = (params: SearchParams = {}) => {
  return fetchData(`v2/search/courses_count_v2/`, params);
};

export const getShortCoursesCountReq = (params: SearchParams = {}) => {
  return fetchData(`v2/search/short-courses-count/`, params);
};

export const searchEnglishCourseReq = (
  params: Record<string, string | number>,
) => {
  return fetchData(`v2/search/short-courses/`, params, "GET", {}, true);
};

export const getInstituteReq = (params: Record<string, string | number>) => {
  return fetchData(`v2/search/institutes_v2/`, params);
};

export const getSpecializationReq = (
  params: Record<string, string | number>,
) => {
  return fetchData(`v2/search/specializations_v2/`, params);
};

export const getSearchUniversitiesReq = (
  params: Record<string, string | number>,
) => {
  return fetchData(`v2/search/institutes-v1/`, params);
};

export const getCourseScaleReq = () => {
  return fetchData(`v2/search/short-courses/scales/`);
};

export const getEnglishCourseDetails = (
  slug: string,
  duration_id: number,
  fee_id: number,
  scale_id: number,
) => {
  if (!slug || !duration_id || !fee_id || !scale_id) {
    throw new Error("Missing required parameters");
  }
  return fetchData(
    `v2/search/short-courses/${slug}/${duration_id}/${fee_id}/${scale_id}`,
  );
};

export const getShortCoursesReq = () => {
  return fetchData(`v2/search/short-courses/`);
};

export const getFeeRangeReq = () => {
  return fetchData(`v2/search/short-courses/fee_range_max/`);
};

export const maxFeeReq = () => {
  return fetchData(`v2/search/fee_range_max/`);
};

export const getScholarshipsReq = (params: Record<string, string | number>) => {
  return fetchData(`v2/search/scholarship-v2`, params);
};
export const getScholarships = () => {
  return fetchData("v2/search/scholarship-v2");
};

export const getScholarshipTypes = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_types`);
};

export const getScholarshipMonths = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_months`);
};

export const getScholarshipYears = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_years`);
};

export const getScholarshipDegreeLevel = () => {
  return fetchData(
    `v2/search/scholarship-v2/scholarship_related_degree_levels`,
  );
};

export const getScholarshipDisciplines = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_disciplines`);
};

export const getScholarshipLocation = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_countries`);
};

export const getUniversityReq = (params: Record<string, string | number>) => {
  return fetchData(`v2/search/institutes-v1`, params);
};

export const getSearchSuggestionsReq = (query: string) => {
  return fetchData(`v2/search/suggestions/`, { q: query });
};

export const getDisciplineSuggestionsReq = (query: string) => {
  return fetchData(`v2/search/search-disciplines-specializations/`, {
    q: query,
  });
};

export const getCompareCoursesReq = (courseSlug: string) => {
  return fetchData(`course/${courseSlug}/course_compare/`);
};

export const getAllCountries = () => {
  return fetchData(`country/all-countries/`);
};

export const getCountryDetails = (slug: string) => {
  return fetchData(`v2/search/countries/${slug}`, {}, "GET", undefined, true);
};

export const getAllCurrency = () => {
  return fetchData(`currency/all/`);
};

export const getAllCurrenciesReq = () => {
  return fetchData(`v2/search/currency/`);
};

export const getAllCountriesReq = () => {
  return fetchData("country/all-countries/");
};

export const getUserInterestsReq = () => {
  return fetchData("v2/search/user_interests/", {}, "GET", {}, true);
};

export const userInterestsReq = (payload: Record<string, unknown>) => {
  return fetchData("v2/search/user_interests/", {}, "POST", payload, true);
};
export const getTopPicks = () => {
  return fetchData(`v2/search/top-picks/`);
};

export const getTopInstitutes = () => {
  return fetchData(`v2/search/top-institutes/`);
};

export const getSearchCountries = () => {
  return fetchData(`search-course/countries/`);
};

export const getSearchDisciplines = (countryId: number) => {
  return fetchData(`search-course/country/${countryId}/disciplines/`);
};

export const getSearchDegreeLevels = (
  countryId: number,
  disciplineId: number,
) => {
  return fetchData(
    `search-course/country/${countryId}/discipline/${disciplineId}/degree_levels/`,
  );
};

export const getSearchCampuses = (
  countryId: number,
  disciplineId: number,
  degreeLevelId: number,
) => {
  return fetchData(
    `search-course/country/${countryId}/discipline/${disciplineId}/degree_level/${degreeLevelId}/campuses/`,
  );
};

export const getSearchSpecializations = (
  countryId: number,
  disciplineId: number,
  degreeLevelId: number,
  campusId: number,
) => {
  return fetchData(
    `search-course/country/${countryId}/discipline/${disciplineId}/degree_level/${degreeLevelId}/campus/${campusId}/specializations/`,
  );
};

export const getSearchCourses = (
  countryId: number,
  disciplineId: number,
  degreeLevelId: number,
  campusId: number,
  specializationId: number,
) => {
  return fetchData(
    `search-course/country/${countryId}/discipline/${disciplineId}/degree_level/${degreeLevelId}/campus/${campusId}/specialization/${specializationId}/courses/`,
  );
};

export const getCompareScholarshipsReq = (scholarshipSlugs: string[]) => {
  const slugsParam = scholarshipSlugs.join(",");
  return fetchData(`v2/search/scholarship-v2/compare/?slugs=${slugsParam}`);
};

export const getCompareUniversitiesReq = (slugs: string[]) => {
  const slugsParam = slugs.join(",");
  return fetchUniversitiesData(
    `v1/universities/tcf/compare?slugs=${encodeURIComponent(slugsParam)}`,
  );
};

export const getUniversalSearchReq = (searchQuery: string) => {
  return fetchData(
    `v2/search/browse-site/?q=${encodeURIComponent(searchQuery)}`,
  );
};

export const getRankingReq = () => {
  return fetchData(`v2/search/max-rankings/`);
};

export const getLocationSuggestionsReq = (query: string) => {
  return fetchData("location-auto-suggestion", {
    search: query,
  });
};

export const getSpecializationsReq = () => {
  return fetchData(`v2/search/specializations_v2/`);
};

export const getInstitutesReq = () => {
  return fetchData(`v2/search/institutes_v2/`);
};

export const getScholarshipTypeReq = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_types`);
};
export const getScholarshipCountryReq = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_countries`);
};

export const getScholarshipMonthReq = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_months`);
};

export const getScholarshipYearReq = () => {
  return fetchData(`v2/search/scholarship-v2/scholarship_related_years`);
};
